import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Subscription, take, shareReplay } from 'rxjs';
import { RoleFilterService } from 'src/app/services/role-filter.service';

import {
  OptionItem,
  HierarchicalData,
  RoleFilterSelected,
  RoleFilterLoadState
} from './role-filter.interfaces';

export {
  User,
  Center,
  CallCenter,
  SubGroup,
  Group,
  GroupsResponse,
  OptionItem,
  HierarchicalData,
  RoleFilterSelected,
  RoleFilterLoadState
} from './role-filter.interfaces';

@Component({
  selector: 'app-role-filter-dropdown-row',
  templateUrl: './role-filter-dropdown-row.component.html',
  styleUrls: ['./role-filter-dropdown-row.component.scss']
})
export class RoleFilterDropdownRowComponent {

  @Input() RoleFilterSelected: RoleFilterSelected = this.RoleFilterService.getEmptyRoleFilter()

  @Output() filterEmitter = new EventEmitter<RoleFilterSelected>()
  @Output() loadStateEmitter = new EventEmitter<RoleFilterLoadState>()

  ShowSubGroupDropdown: boolean = false
  ShowCallCentersDropdown: boolean = false
  ShowCenterDropdown: boolean = false
  ShowUserDropdown: boolean = false

  SubgroupArray: OptionItem[] = []
  CallCenterArray: OptionItem[] = []
  CenterArray: OptionItem[] = []
  UserArray: OptionItem[] = []

  ShowComponent: boolean = false;
  loading: boolean = true;
  private loadRequestId: number = 0;
  Subscription: Subscription | undefined;

  HierarchicalData: HierarchicalData = {
    subGroups: [],
    centersBySubGroup: {},

    callCentersBySubGroup: {},
    centersByCallCenters: {},
    usersByCallCenters: {},

    usersByCenter: {},
    allCallCenters: [],
    allCenters: [],
    allUsers: [],
    _validGroupCount: 0,
    _validSubGroupCount: 0,
    _validCenterCount: 0
  };

  constructor(
    private RoleFilterService: RoleFilterService,
  ) {
  }

  emitFilter() {
    this.filterEmitter.emit(this.RoleFilterSelected);
  }

  ngOnInit() {
    this.setData();
  }

  ngOnChanges() {
  }
  ngOnDestroy() {
    this.Subscription?.unsubscribe();
  }

  public async setData() {

    let hasError = false;
    this.loadStateEmitter.emit({ resolved: false, hasError: false });
    try {
      this.loading = true;
      this.Subscription = this.RoleFilterService.getRoleFilterData().pipe(take(1), shareReplay(1)).subscribe({
        next: (data) => {

          if (!data) {
            hasError = true;
            this.ShowComponent = false;
            this.loading = false;
            this.loadStateEmitter.emit({ resolved: true, hasError });
            return;
          }
          const converted: HierarchicalData = data;
          this.HierarchicalData = converted;
          // Visibilidad de dropdowns
          const validSubGroups = (converted as any)._validSubGroupCount > 1;
          const validCallCenters = (converted as any)._validCallCenterCount > 1;
          const validCenters = (converted as any)._validCenterCount > 1;

          this.ShowSubGroupDropdown = validSubGroups;
          this.ShowCallCentersDropdown = validCallCenters;
          this.ShowCenterDropdown = validCenters;

          // inicializar arrays visibles
          this.SubgroupArray = this.HierarchicalData.subGroups.slice();
          this.CallCenterArray = [...this.HierarchicalData.allCallCenters];
          this.CenterArray = this.getAvailableCenters();
          this.UserArray = this.getAvailableUsers();
          this.ShowUserDropdown = this.UserArray.length >= 1;
          this.ShowComponent = true;

          this.loading = false;
          this.loadStateEmitter.emit({ resolved: true, hasError });
        },
        error: (error) => {
          hasError = true;
          this.ShowComponent = false;
          this.loading = false;
          console.log('[RoleFilterDropdownRow] Error loading role filters', error);
          this.loadStateEmitter.emit({ resolved: true, hasError });
        }
      });
    }
    catch (error) {
      hasError = true;
      this.ShowComponent = false;
      this.loading = false;
      this.loadStateEmitter.emit({ resolved: true, hasError });
    }
  }

  /**
   * Devuelve los centros que deberían mostrarse en el dropdown, respetando reglas:
   * - Si no existen subGroups en los datos (array vacío), se muestran todos los centros.
   * - Si hay SubGroups disponibles y hay subgroups seleccionados, se muestran centros de esos subgroups.
   * - Si no hay subgroups seleccionados, se muestran todos los centros.
   */

  private getAvailableCenters(): OptionItem[] {
    const allCenters = this.HierarchicalData.allCenters || [];

    // Si no hay datos → todos
    if (!this.HierarchicalData.subGroups?.length && !this.HierarchicalData.allCallCenters?.length) {
      return this.sortByName(allCenters.slice());
    }

    // ✅ PRIORIDAD 1: CallCenters seleccionados
    if (this.RoleFilterSelected.CallCenters?.length > 0) {
      const callCenterIds = this.RoleFilterSelected.CallCenters.map(cc => cc.id);

      const centers = callCenterIds.flatMap(id =>
        this.HierarchicalData.centersByCallCenters[id] || []
      );

      const map = new Map<string, OptionItem>();
      centers.forEach(c => map.set(c.id, c));

      return this.sortByName([...map.values()]);
    }

    // ✅ PRIORIDAD 2: SubGroups seleccionados
    if (this.RoleFilterSelected.SubGroups?.length > 0) {
      const subGroupIds = this.RoleFilterSelected.SubGroups.map(sg => sg.id);

      const centers = subGroupIds.flatMap(id =>
        this.HierarchicalData.centersBySubGroup[id] || []
      );

      const map = new Map<string, OptionItem>();
      centers.forEach(c => map.set(c.id, c));

      return this.sortByName([...map.values()]);
    }

    // ✅ Por defecto todos
    return this.sortByName(allCenters.slice());
  }

  /**
   * Devuelve los usuarios que deberían mostrarse en el dropdown, respetando reglas:
   * - Si hay centros seleccionados -> usuarios de esos centros.
   * - Else si hay subgroups seleccionados -> usuarios de los centros de esos subgroups.
   * - Si no hay info de subgroups (array vacío) y no hay centros seleccionados -> todos los usuarios.
   */

  private getAvailableUsers(): OptionItem[] {
    const allUsers = this.HierarchicalData.allUsers || [];

    // ✅ PRIORIDAD 1: Centers
    if (this.RoleFilterSelected.Centers?.length > 0) {
      const centerIds = this.RoleFilterSelected.Centers.map(c => c.id);

      const users = centerIds.flatMap(id =>
        this.HierarchicalData.usersByCenter[id] || []
      );

      const map = new Map<string, OptionItem>();
      users.forEach(u => map.set(u.id, u));

      return this.sortByName([...map.values()]);
    }

    // ✅ PRIORIDAD 2: Call Centers (+ usersByCallCenterId)
    if (this.RoleFilterSelected.CallCenters?.length > 0) {
      const callCenterIds = this.RoleFilterSelected.CallCenters.map(cc => cc.id);

      // 1. Obtener centros asociados a esos call centers
      const centerIds = callCenterIds.flatMap(id =>
        (this.HierarchicalData.centersByCallCenters[id] || []).map(c => c.id)
      );

      // 2. Usuarios por centro
      const usersFromCenters = centerIds.flatMap(id =>
        this.HierarchicalData.usersByCenter[id] || []
      );

      // 3. Usuarios directos por call center
      const usersFromCallCenters = callCenterIds.flatMap(id =>
        this.HierarchicalData.usersByCallCenters?.[id] || []
      );

      // 4. Unir y eliminar duplicados
      const map = new Map<string, OptionItem>();
      [...usersFromCenters, ...usersFromCallCenters].forEach(u => map.set(u.id, u));

      return this.sortByName([...map.values()]);
    }

    // ✅ PRIORIDAD 3: SubGroups
    if (this.RoleFilterSelected.SubGroups?.length > 0) {
      const subGroupIds = this.RoleFilterSelected.SubGroups.map(sg => sg.id);

      const centerIds = subGroupIds.flatMap(id =>
        (this.HierarchicalData.centersBySubGroup[id] || []).map(c => c.id)
      );

      const users = centerIds.flatMap(id =>
        this.HierarchicalData.usersByCenter[id] || []
      );

      const map = new Map<string, OptionItem>();
      users.forEach(u => map.set(u.id, u));

      return this.sortByName([...map.values()]);
    }

    return this.sortByName(allUsers.slice());
  }

  /***********************
   *  EVENT HANDLERS
   ***********************/

  onSubGroupChange(selectedIdsSubgroups: OptionItem[]) {
    // 1. Actualizar SubGroups seleccionados
    this.RoleFilterSelected.SubGroups = selectedIdsSubgroups ? [...selectedIdsSubgroups] : [];

    // 2. Recalcular Call Centers disponibles en base a SubGroups
    if (this.RoleFilterSelected.SubGroups.length > 0) {

      const subGroupIds = this.RoleFilterSelected.SubGroups.map(sg => sg.id);

      const callCenters = subGroupIds.flatMap(id =>
        this.HierarchicalData.callCentersBySubGroup[id] || []
      );

      // Eliminar duplicados
      const callCenterMap = new Map<string, OptionItem>();
      callCenters.forEach(cc => callCenterMap.set(cc.id, cc));

      this.CallCenterArray = this.sortByName([...callCenterMap.values()]);

    } else {
      // Si no hay subgrupos seleccionados -> todos los call centers
      this.CallCenterArray = this.sortByName([...this.HierarchicalData.allCallCenters]);
    }

    // 3. Limpiar CallCenters seleccionados que ya no existen
    this.RoleFilterSelected.CallCenters =
      (this.RoleFilterSelected.CallCenters || [])
        .filter(cc => this.CallCenterArray.some(av => av.id === cc.id));

    // 4. Recalcular Centers disponibles
    this.CenterArray = this.getAvailableCenters();

    // 5. Limpiar Centers inválidos
    this.RoleFilterSelected.Centers =
      (this.RoleFilterSelected.Centers || [])
        .filter(c => this.CenterArray.some(av => av.id === c.id));

    // 6. Recalcular Users disponibles
    this.UserArray = this.getAvailableUsers();

    // 7. Limpiar Users inválidos
    this.RoleFilterSelected.Users =
      (this.RoleFilterSelected.Users || [])
        .filter(u => this.UserArray.some(av => av.id === u.id));

    // 8. Emitir cambios
    this.emitFilter();
  }
  onCallCenterChange(selectedCallCenterIds: OptionItem[]) {
    this.RoleFilterSelected.CallCenters = selectedCallCenterIds ? [...selectedCallCenterIds] : [];

    this.CenterArray = this.getAvailableCenters();

    this.RoleFilterSelected.Centers =
      this.RoleFilterSelected.Centers?.filter(c =>
        this.CenterArray.some(av => av.id === c.id)
      );

    this.UserArray = this.getAvailableUsers();

    this.RoleFilterSelected.Users =
      this.RoleFilterSelected.Users?.filter(u =>
        this.UserArray.some(av => av.id === u.id)
      );

    this.emitFilter();
  }
  onCenterChange(selectedCenters: OptionItem[]) {
    // actualizar selección
    this.RoleFilterSelected.Centers = selectedCenters ? selectedCenters.slice() : [];

    // recalcular arrays disponibles: los centros se deben limitar a lo disponible actualmente (por subgroups si los hay)
    this.CenterArray = this.getAvailableCenters();

    // recalcular usuarios según centros seleccionados (prioridad a centros)
    this.UserArray = this.getAvailableUsers();
    this.RoleFilterSelected.Users = (this.RoleFilterSelected.Users || [])
      .filter(u => this.UserArray.some(av => av.id === u.id));

    // emitir (no llamamos a onSubGroupChange para evitar reentradas; simplemente sincronizamos arrays)
    this.emitFilter();
  }
  onUserChange(selectedUsers: OptionItem[]) {
    this.RoleFilterSelected.Users = selectedUsers ? [...selectedUsers] : [];

    // Solo recalcular arrays
    this.CenterArray = this.getAvailableCenters();
    this.UserArray = this.getAvailableUsers();

    // Eliminar users inválidos si el contexto cambió
    this.RoleFilterSelected.Users =
      this.RoleFilterSelected.Users.filter(u =>
        this.UserArray.some(av => av.id === u.id)
      );

    this.emitFilter();
  }

  /***********************
   *  UTIL
   ***********************/
  sortByName<T extends { name?: string }>(array: T[]): T[] {
    return array.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }
  clearFilter() {
    try {
      const hasSelection =
        this.RoleFilterSelected.Groups!.length > 0 ||
        this.RoleFilterSelected.SubGroups!.length > 0 ||
        this.RoleFilterSelected.Centers!.length > 0 ||
        this.RoleFilterSelected.Users!.length > 0;

      if (!hasSelection) {
        return;
      }
    } catch {
      console.error("Error checking empty filters")
    }

    this.RoleFilterSelected = this.RoleFilterService.getEmptyRoleFilter()
    this.CenterArray = this.getAvailableCenters();
    this.UserArray = this.getAvailableUsers();
    this.emitFilter()
  }

}








